export interface IRepeatingActionInfo
{
	id: NodeJS.Timeout;
	interval: number;
}

export class RepeatingActionsManager 
{
	static activeActions = new Map<string, IRepeatingActionInfo>();

	/**
	 * Sets a repeating action to be executed at the specified interval, using setInterval
	 *
	 * @static
	 * @param {() => any} action The action to be executed
	 * @param {number} [interval=60000] The interval in milliseconds at which the action should be executed. Defaults to 60000ms (1 minute)
	 * @param {string} [name] The name of the action. If not provided, the name of the function will be used
	 * @memberof RepeatingActionsManager
	 */
	static setAction(action: () => any, interval = 60000, name?: string) 
	{
		const actionName = name || action.name;
		this.removeAction(action);
		this.activeActions.set(actionName, { id: setInterval(action, interval), interval: interval });
	}

	/**
	 * Removes a repeating action from the manager by clearing the interval associated with it
	 *
	 * @static
	 * @param {(string | (() => any))} action The action to remove, either by name or function reference (same function reference used in setAction)
	 * @memberof RepeatingActionsManager
	 */
	static removeAction(action: string | (() => any))
	{
		const actionName = typeof action === "string" ? action : action.name;

		const actionInfo = this.activeActions.get(actionName);
		if (actionInfo)
		{
			clearInterval(actionInfo.id);
			this.activeActions.delete(actionName);
		}
	}
	
	/**
	 * Gets the information about a repeating action
	 *
	 * @static
	 * @param {(string | (() => any))} action The action to get information about, either by name or function reference (same function reference used in setAction)
	 * @return {IRepeatingActionInfo} The information about the action
	 * @memberof RepeatingActionsManager
	 */
	static getActionInfo(action: string | (() => any))
	{
		const actionName = typeof action === "string" ? action : action.name;
		return this.activeActions.get(actionName);
	}
}
